import type { Rarity } from '@/game/chests';

import type { ChestScene } from './chest-scene';

/**
 * A chest as a still image (the same 3D mesh and material as the arrival and
 * the reveal), for places that show a chest without a stage: the sign-up
 * sheet's locked chest. Baked once per rarity and size, one at a time. Loaded
 * lazily so the shell's first paint never waits on Pixi; `null` when there is
 * no WebGL (the caller keeps its flat glyph). Blockari's `render/chest-still.ts`.
 *
 * ONE small renderer is made for the first bake and KEPT. Destroying a Pixi
 * renderer tears down state the others share: a still baked while the chest
 * stage was drawing (the sheet opens over it) took the stage's own next frame
 * down with it ("Cannot read properties of null (reading 'clear')"). A kept
 * 168-px renderer costs one small GL context for the session and cannot do that.
 */

const cache = new Map<string, Promise<string | null>>();
let chain: Promise<unknown> = Promise.resolve();
let scene: Promise<ChestScene | null> | null = null;

function baker(): Promise<ChestScene | null> {
  scene ??= (async () => {
    try {
      const { ChestScene: Scene } = await import('./chest-scene');
      const s = new Scene('medium', 0);
      // Never attached to the page: it only ever renders to be read back.
      await s.init(document.createElement('div'), 168, 168);
      return s;
    } catch {
      return null;
    }
  })();
  return scene;
}

async function bake(rarity: Rarity, px: number): Promise<string | null> {
  const s = await baker();
  if (!s) return null;
  try {
    return s.bakeStill(rarity, px);
  } catch {
    return null;
  }
}

export function chestStill(rarity: Rarity, px: number): Promise<string | null> {
  const key = `${rarity}|${px}`;
  let p = cache.get(key);
  if (!p) {
    p = chain.then(() => bake(rarity, px));
    chain = p.catch(() => null);
    cache.set(key, p);
  }
  return p;
}
